import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
    ThemeEvents,
    CartUpdateEvent,
    QuantitySelectorUpdateEvent,
    CartAddEvent,
    DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Reads quantity directly from the quantity input in the cart row.
 * @param {Element|null} row
 * @returns {number}
 */
function getQuantityFromRow(row) {
    const input = row?.querySelector('quantity-selector-component input[name="updates[]"]');
    const value = input instanceof HTMLInputElement ? parseInt(input.value) : 1;
    return isNaN(value) || value < 1 ? 1 : value;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
    #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

    connectedCallback() {
        super.connectedCallback();

        document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
        document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
        document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

        // Delegated listener for select[name="update_selling_plan"]
        // Attached here so it survives morphSection re-renders
        this.addEventListener('change', this.#handleSellingPlanSelectChange);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
        document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
        this.removeEventListener('change', this.#handleSellingPlanSelectChange);
    }

    /**
     * Handles QuantitySelectorUpdateEvent change event.
     * @param {QuantitySelectorUpdateEvent} event - The event.
     */
    #onQuantityChange(event) {
        const { quantity, cartLine: line } = event.detail;

        if (!line) return;

        if (quantity === 0) {
            return this.onLineItemRemove(line);
        }

        this.updateQuantity({ line, quantity, action: 'change' });

        const lineItemRow = this.refs.cartItemRows[line - 1];
        if (!lineItemRow) return;

        const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
        textComponent?.shimmer();
    }

    /**
     * Handles the line item removal.
     * @param {number} line - The line item index.
     */
    onLineItemRemove(line) {
        this.updateQuantity({ line, quantity: 0, action: 'clear' });

        const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
        if (!cartItemRowToRemove) return;

        const rowsToRemove = [
            cartItemRowToRemove,
            ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
        ];

        rowsToRemove.forEach((row) => {
            const remove = () => row.remove();
            if (prefersReducedMotion()) return remove();
            row.style.setProperty('--row-height', `${row.clientHeight}px`);
            row.classList.add('removing');
            onAnimationEnd(row, remove);
        });
    }

    /**
     * Updates the quantity — core cart change method.
     * Also used by selling plan upgrade to reuse shimmer + morph + event flow.
     *
     * @param {Object} config
     * @param {number} config.line
     * @param {number} config.quantity
     * @param {string} config.action
     * @param {string} [config.selling_plan] - Optional: pass to update selling plan on the line
     */
    updateQuantity(config) {
        const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

        this.#disableCartItems();

        const { line, quantity } = config;
        const { cartTotal } = this.refs;

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionsToUpdate = new Set([this.sectionId]);
        cartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement && item.dataset.sectionId) {
                sectionsToUpdate.add(item.dataset.sectionId);
            }
        });

        const payload = {
            line,
            quantity,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
        };

        if (config.selling_plan !== undefined) {
            payload.selling_plan = config.selling_plan;
        }

        const body = JSON.stringify(payload);
        cartTotal?.shimmer();

        fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
            .then((response) => response.text())
            .then((responseText) => {
                const parsedResponseText = JSON.parse(responseText);

                resetShimmer(this);

                if (parsedResponseText.errors) {
                    this.#handleCartError(line, parsedResponseText);
                    return;
                }

                const newSectionHTML = new DOMParser().parseFromString(
                    parsedResponseText.sections[this.sectionId],
                    'text/html'
                );

                const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
                const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

                this.dispatchEvent(
                    new CartUpdateEvent({}, this.sectionId, {
                        itemCount: newCartItemCount,
                        source: 'cart-items-component',
                        sections: parsedResponseText.sections,
                    })
                );

                morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
                window.updateCartComparePrices();
            })
            .catch((error) => {
                console.error(error);
                resetShimmer(this);
            })
            .finally(() => {
                this.#enableCartItems();
                cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
            });
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Selling plan select handler
    // ─────────────────────────────────────────────────────────────────────────

    #handleSellingPlanSelectChange = (event) => {
        const select = event.target;

        if (!(select instanceof HTMLSelectElement) || select.name !== 'update_selling_plan') return;

        const lineIndex = parseInt(select.dataset.index);
        const newPlanId = select.value;
        const isOneTime = newPlanId === '0';

        if (!lineIndex) return;

        const row = select.closest('tr');
        const quantity = getQuantityFromRow(row);

        this.updateQuantity({
            line: lineIndex,
            quantity,
            action: 'change',
            selling_plan: isOneTime ? '' : newPlanId,
        });

        const lineItemRow = this.refs.cartItemRows[lineIndex - 1];
        if (!lineItemRow) return;
        const textComponent = /** @type {TextComponent | undefined} */ (
            lineItemRow.querySelector('text-component')
        );
        textComponent?.shimmer();
    };

    // ─────────────────────────────────────────────────────────────────────────

    handleDiscountUpdate = (event) => {
        this.#handleCartUpdate(event);
    };

    #handleCartError = (line, parsedResponseText) => {
        const quantitySelector = this.refs.quantitySelectors[line - 1];
        const quantityInput = quantitySelector?.querySelector('input');

        if (!quantityInput) throw new Error('Quantity input not found');

        quantityInput.value = quantityInput.defaultValue;

        const cartItemError = this.refs[`cartItemError-${line}`];
        const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

        if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
        if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

        cartItemError.textContent = parsedResponseText.errors;
        cartItemErrorContainer.classList.remove('hidden');
    };

    #handleCartUpdate = (event) => {
        if (event instanceof DiscountUpdateEvent) {
            sectionRenderer.renderSection(this.sectionId, { cache: false });
            return;
        }
        if (event.target === this) return;

        const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
        if (cartItemsHtml) {
            morphSection(this.sectionId, cartItemsHtml);
        } else {
            sectionRenderer.renderSection(this.sectionId, { cache: false });
        }
    };

    #disableCartItems() {
        this.classList.add('cart-items-disabled');
    }

    #enableCartItems() {
        this.classList.remove('cart-items-disabled');
    }

    get sectionId() {
        const { sectionId } = this.dataset;
        if (!sectionId) throw new Error('Section id missing');
        return sectionId;
    }
}

if (!customElements.get('cart-items-component')) {
    customElements.define('cart-items-component', CartItemsComponent);
}

// ─────────────────────────────────────────────────────────────────────────────
// subscribe-upgrade-button
// ─────────────────────────────────────────────────────────────────────────────

class SubscribeUpgradeButton extends Component {
    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();

        const button = this.querySelector('button.upgrade_selling_plan');
        button?.addEventListener('click', this.#handleClick, {
            signal: this.#abortController.signal,
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    #handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const lineIndex = parseInt(this.dataset.index);
        const sellingPlanId = this.dataset.subscriptionId;

        if (!lineIndex || !sellingPlanId) {
            console.error('SubscribeUpgradeButton: missing data-index or data-subscription-id');
            return;
        }

        const cartItemsComponent = /** @type {CartItemsComponent|null} */ (
            this.closest('cart-items-component')
        );

        if (!cartItemsComponent) {
            console.error('SubscribeUpgradeButton: no parent cart-items-component found');
            return;
        }

        const row = this.closest('tr');
        const quantity = getQuantityFromRow(row);

        cartItemsComponent.updateQuantity({
            line: lineIndex,
            quantity,
            action: 'change',
            selling_plan: sellingPlanId,
        });

        const lineItemRow = cartItemsComponent.refs.cartItemRows[lineIndex - 1];
        if (!lineItemRow) return;
        const textComponent = /** @type {TextComponent | undefined} */ (
            lineItemRow.querySelector('text-component')
        );
        textComponent?.shimmer();
    };
}

if (!customElements.get('subscribe-upgrade-button')) {
    customElements.define('subscribe-upgrade-button', SubscribeUpgradeButton);
}